import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import Spinner from "../components/Spinner";
import { fetchStudies, userByStudy, fetchActivitySummaries, updateActivitySummaryDate, fetchAnalysisAuditLogs, addAnalysisAuditLog, saveBaseline, fetchBaseline, renameBaseline, deleteBaseline } from "../services/apiServices";
import { TbLogs } from "react-icons/tb";
import { MdClearAll } from "react-icons/md";
import { AnalysisLogsTable } from "../components/tables/AnalysisLogsTable";
import { generateUserAnalysisPDF } from "../report-logic/user-analysis-pdf";
import RangeDeSelectCalendar from "../components/RangeDeSelectCalendar";
import { MdCalendarToday, MdExpandMore, MdExpandLess } from "react-icons/md";
import { BaselineSection } from "../components/BaselineSection";
import { toast } from "react-toastify";
import "../assets/css/UserTable.css";

// Read the logged-in username from the JWT token
function getLoggedInUserInfo() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return { name: "", email: "" };
    const decoded = jwtDecode(token);
    return { name: decoded?.name || decoded?.sub || "Admin", email: decoded?.email || "" };
  } catch (_) {
    return { name: "", email: "" };
  }
}

const getLoggedInUser = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const decoded = jwtDecode(token);
    return decoded?.email || decoded?.name || decoded?.sub || null;
  } catch (_) {
    return null;
  }
};

const formatLocalISO = (date) => {
  if (!date || isNaN(new Date(date instanceof Date ? date : date + "T00:00:00").getTime())) return "";
  const d = date instanceof Date ? date : new Date(date + "T00:00:00");
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Redundant BaselineRecord removed (now in BaselineHistorySection.js)

export const Analysis = () => {
  const { studyId, siteId, userId } = useParams();
  const decodedUserId = useMemo(() => decodeURIComponent(userId || ""), [userId]);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [patientInfo, setPatientInfo] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [customReasonDates, setCustomReasonDates] = useState(new Set());
  const [startDate, setStartDate] = useState(() => {
    const s = localStorage.getItem(`startDate_${decodedUserId}`);
    if (s) return s;
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 7); // 7 days ago (Yesterday - 6)
    return formatLocalISO(d);
  });
  const [endDate, setEndDate] = useState(() => {
    const e = localStorage.getItem(`endDate_${decodedUserId}`);
    if (e) return e;
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 1); // Yesterday
    return formatLocalISO(d);
  });

  const [excludeModalDate, setExcludeModalDate] = useState(null);
  const [excludeReason, setExcludeReason] = useState("");
  const [otherReasonText, setOtherReasonText] = useState("");
  const [globalDeselected, setGlobalDeselected] = useState(new Set());
  const [globalAdded, setGlobalAdded] = useState(new Set());
  const [globalAutoExclude, setGlobalAutoExclude] = useState(() => {
    try { return localStorage.getItem(`autoExclude_${decodedUserId}`) === "true"; } catch { return false; }
  });
  const [expandedLogs, setExpandedLogs] = useState({});
  const [showCalendar, setShowCalendar] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [pdfFileName, setPdfFileName] = useState("");
  const [baselines, setBaselines] = useState([]);
  const [savingBaseline, setSavingBaseline] = useState(false);
  const [showBaselineHistory, setShowBaselineHistory] = useState(false);
  const [showAnalysisTable, setShowAnalysisTable] = useState(true);
  const [showSaveBaselineModal, setShowSaveBaselineModal] = useState(false);
  const [newBaselineName, setNewBaselineName] = useState("");
  const [saveBaselineError, setSaveBaselineError] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [showCompliancePromptModal, setShowCompliancePromptModal] = useState(false);
  const [baselineToDownload, setBaselineToDownload] = useState(null);

  const capitalize = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const toggleLogs = (date) => {
    setExpandedLogs((prev) => {
      const isExpanded = prev[date]?.expanded || false;
      if (isExpanded) {
        return { ...prev, [date]: { ...prev[date], expanded: false } };
      }
      if (!prev[date]?.logs) {
        fetchAnalysisAuditLogs(decodeURIComponent(userId)).then((logs) => {
          const validLogs = Array.isArray(logs) ? logs : (logs?.data || []);
          const filteredLogs = validLogs.filter(log => log.date === date || !log.date);
          setExpandedLogs((current) => ({
            ...current,
            [date]: { logs: filteredLogs, loading: false, expanded: true },
          }));
        });
        return {
          ...prev,
          [date]: { logs: [], loading: true, expanded: true },
        };
      }
      return { ...prev, [date]: { ...prev[date], expanded: true } };
    });
  };

  const loggedInUser = getLoggedInUser();
  const loggedInUserEmail = getLoggedInUserInfo().email;
const isAdmin = loggedInUserEmail === "admin@fempulse.com";

  // Initial range now handled by useState initializers.

  useEffect(() => {
    try {
      const stored = localStorage.getItem(`excludedDates_${decodedUserId}`);
      if (stored) {
        setGlobalDeselected(new Set(JSON.parse(stored)));
      } else {
        setGlobalDeselected(new Set());
      }

      const storedAdded = localStorage.getItem(`addedDates_${decodedUserId}`);
      if (storedAdded) {
        setGlobalAdded(new Set(JSON.parse(storedAdded)));
      } else {
        setGlobalAdded(new Set());
      }
      const storedAutoExclude = localStorage.getItem(`autoExclude_${decodedUserId}`);
      setGlobalAutoExclude(storedAutoExclude === "true");

      const s = localStorage.getItem(`startDate_${decodedUserId}`);
      const e = localStorage.getItem(`endDate_${decodedUserId}`);

      if (s) {
        setStartDate(s);
      } else {
        // Default: 7 days ago
        const defaultStart = new Date();
        defaultStart.setHours(0, 0, 0, 0);
        defaultStart.setDate(defaultStart.getDate() - 7);
        setStartDate(formatLocalISO(defaultStart));
      }

      if (e) {
        setEndDate(e);
      } else {
        // Default: yesterday
        const defaultEnd = new Date();
        defaultEnd.setHours(0, 0, 0, 0);
        defaultEnd.setDate(defaultEnd.getDate() - 1);
        setEndDate(formatLocalISO(defaultEnd));
      }

    } catch (e) {
      setGlobalDeselected(new Set());
    }
  }, [userId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const studies = await fetchStudies();
      const study = Array.isArray(studies) ? studies.find((s) => String(s.id) === String(studyId)) : null;

      const users = await userByStudy(studyId);
      const user = Array.isArray(users) ? users.find((u) => String(u.id) === decodeURIComponent(userId)) : null;

      if (study && user) {
        setPatientInfo({
          studyName: study.name,
          siteName: decodeURIComponent(siteId),
          patientId: user.email || user.id,
          patientName: user.name || "",
          compliance_status: user.compliance_status ?? "no-data",
          compliance_issues: user.compliance_issues ?? "[]",
          latestActivity: user.latest_activity ?? "No Activity",
        });
      }

      const summaries = await fetchActivitySummaries(decodeURIComponent(userId));
      const summaryArray = Array.isArray(summaries) ? summaries : [];
      setActivityData(summaryArray);

      if (summaryArray.length > 0) {
        // Sort the user's actual record dates descending to find the most recent
        const sortedDates = [...summaryArray]
          .map(a => a.date)
          .filter(Boolean)
          .sort((a, b) => new Date(b + "T00:00:00") - new Date(a + "T00:00:00"));

        // Cap at yesterday so we never show today's incomplete data
        const yesterday = new Date();
        yesterday.setHours(0, 0, 0, 0);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = formatLocalISO(yesterday);

        // Latest record date, but no later than yesterday
        const latestRecordDate = sortedDates[0] && sortedDates[0] <= yesterdayStr
          ? sortedDates[0]
          : yesterdayStr;

        const end = new Date(latestRecordDate + "T00:00:00");
        const start = new Date(end);
        start.setDate(end.getDate() - 6); // last 7 days of user's data

        const startStr = formatLocalISO(start);
        const endStr = formatLocalISO(end);

        setEndDate(endStr);
        setStartDate(startStr);

        setGlobalDeselected(new Set());
        setGlobalAdded(new Set());

        localStorage.setItem(`startDate_${decodedUserId}`, startStr);
        localStorage.setItem(`endDate_${decodedUserId}`, endStr);
        localStorage.removeItem(`excludedDates_${decodedUserId}`);
        localStorage.removeItem(`addedDates_${decodedUserId}`);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchBaseline(decodeURIComponent(userId)).then(data => {
        if (data && Array.isArray(data)) setBaselines(data);
      });
    }
  }, [userId]);

  useEffect(() => {
    if (userId) loadData();
  }, [userId, studyId, siteId]);

  const dateRange = useMemo(() => {
    if (!startDate || !endDate) return [];
    const dates = [];
    let current = new Date(startDate + "T00:00:00");
    const end = new Date(endDate + "T00:00:00");
    if (isNaN(current.getTime()) || isNaN(end.getTime()) || current > end) return [];

    while (current <= end) {
      dates.push(formatLocalISO(current));
      current.setDate(current.getDate() + 1);
      if (dates.length > 90) break; // Safety cap
    }
    // Also include 'Added' days that might be outside the range
    globalAdded.forEach(d => {
      if (!dates.includes(d)) dates.push(d);
    });
    // Sort them
    return dates.sort((a, b) => new Date(b) - new Date(a));
  }, [startDate, endDate, globalAdded]);

  const tableRows = useMemo(() => {
    return dateRange.map(dateStr => {
      const record = activityData.find(a => a.date === dateStr);
      return { date: dateStr, record: record || null };
    });
  }, [dateRange, activityData]);

  const averages = useMemo(() => {
    let voidsSum = 0, leaksSum = 0, intakeSum = 0, nightSum = 0;
    let count = 0;
    tableRows.forEach(row => {
      const rec = row.record;
      const isIncluded = (!rec || rec.dayInclude !== "no") && !globalDeselected.has(row.date);

      if (isIncluded) {
        voidsSum += rec?.voidActivityCount || 0;
        leaksSum += rec?.totalLeaks || 0;
        intakeSum += rec?.intakeMl || 0;
        nightSum += rec?.nightVoids || 0;
        count++;
      }
    });
    if (count === 0) return { voids: 0, leaks: 0, fluid: 0, night: 0 };
    return {
      voids: (voidsSum / count).toFixed(1),
      leaks: (leaksSum / count).toFixed(1),
      fluid: (intakeSum / count).toFixed(0),
      night: (nightSum / count).toFixed(1),
    };
  }, [tableRows, globalDeselected]);

  const handleCalendarConfirm = ({ from, to, deselected, added, autoExclude }) => {
    const fromStr = formatLocalISO(from);
    const toStr = formatLocalISO(to);

    const newDeselected = new Set(deselected.map(d => formatLocalISO(d)));
    const newAdded = new Set((added || []).map(d => formatLocalISO(d)));

    const allIncluded = [];

    let curr = new Date(from);
    const last = new Date(to);
    while (curr <= last) {
      const k = formatLocalISO(curr);
      if (!newDeselected.has(k)) {
        allIncluded.push(k);
      }
      curr.setDate(curr.getDate() + 1);
    }

    newAdded.forEach(k => {
      if (!allIncluded.includes(k)) allIncluded.push(k);
    });

    let finalStart = fromStr;
    let finalEnd = toStr;

    if (allIncluded.length > 0) {
      allIncluded.sort();
      finalStart = allIncluded[0];
      finalEnd = allIncluded[allIncluded.length - 1];
    }

    setStartDate(finalStart);
    setEndDate(finalEnd);
    localStorage.setItem(`startDate_${decodedUserId}`, finalStart);
    localStorage.setItem(`endDate_${decodedUserId}`, finalEnd);

    setGlobalDeselected(newDeselected);
    setGlobalAdded(newAdded);
    setGlobalAutoExclude(!!autoExclude);

    localStorage.setItem(`excludedDates_${decodedUserId}`, JSON.stringify(Array.from(newDeselected)));
    localStorage.setItem(`addedDates_${decodedUserId}`, JSON.stringify(Array.from(newAdded)));
    localStorage.setItem(`autoExclude_${decodedUserId}`, autoExclude ? "true" : "false");
    setShowCalendar(false);
  };

  const handleDayIncludeChange = async (date, newValue, reason = null) => {
    setActivityData(prev => {
      const exists = prev.find(a => a.date === date);
      const updatedFields = {
        dayInclude: newValue,
        dayIncludeUserName: loggedInUser,
        dayexcludeReason: newValue === "yes" ? null : (reason || (exists ? exists.dayexcludeReason : null))
      };
      if (exists) {
        return prev.map(a => a.date === date ? { ...a, ...updatedFields } : a);
      } else {
        return [...prev, { date, voidActivityCount: 0, totalLeaks: 0, intakeMl: 0, nightVoids: 0, totalActivityCount: 0, ...updatedFields }];
      }
    });

    const payload = { dayInclude: newValue, dayIncludeUserName: loggedInUser };
    if (reason) payload.dayexcludeReason = reason;

    try {
      await updateActivitySummaryDate(decodeURIComponent(userId), date, payload);

      const userInfo = getLoggedInUserInfo();
      const exists = activityData.find(a => a.date === date);
      const oldVal = exists?.dayInclude === "no" ? "Exclude" : "Include";
      const newVal = newValue === "no" ? "Exclude" : "Include";

      await addAnalysisAuditLog({
        userId: decodeURIComponent(userId),
        date: date,
        adminEmail: userInfo.email,
        adminName: userInfo.name,
        oldStatus: oldVal,
        newStatus: newVal,
        action: "Status Update",
        source: "Custom Analysis",
        reason: reason || ""
      });

    } catch (err) {
      console.error(err);
      alert("Failed to update status");
      loadData();
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T00:00:00");
    const currentYear = new Date().getFullYear();
    if (d.getFullYear() !== currentYear) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getDisplayDates = () => {
    let minD = startDate;
    let maxD = endDate;
    if (globalAdded && globalAdded.size > 0) {
      globalAdded.forEach(d => {
        if (d < minD) minD = d;
        if (d > maxD) maxD = d;
      });
    }
    return { start: minD, end: maxD };
  };

  const handleDownloadPDF = async () => {
    const formatPDFDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr + "T00:00:00");
      return `${d.toLocaleDateString('en-US', { month: 'short' })}-${d.getDate()}-${d.getFullYear()}`;
    };
    const safePatientName = (patientInfo?.patientName || patientInfo?.patientId || "patient").replace(/[\s_]+/g, "-").replace(/[^a-zA-Z0-9@.-]/g, "");
    const displayDates = getDisplayDates();
    const dateRangeStr = `${formatPDFDate(displayDates.start)}-to-${formatPDFDate(displayDates.end)}`;
    setPdfFileName(`${safePatientName}-summary-${dateRangeStr}`);
    setShowDownloadModal(true);
  };

  const triggerSaveBaseline = () => {
    const formatBaselineDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr + "T00:00:00");
      return `${d.toLocaleDateString('en-US', { month: 'short' })}-${d.getDate()}-${d.getFullYear()}`;
    };
    const displayDates = getDisplayDates();
    const dateRangeStr = `${formatBaselineDate(displayDates.start)}-to-${formatBaselineDate(displayDates.end)}`;
    setNewBaselineName(`Custom-name-${dateRangeStr}`);
    setSaveBaselineError("");
    setShowSaveBaselineModal(true);
  };

  const handleSaveBaseline = async (recordName) => {
    try {
      setSavingBaseline(true);
      setSaveBaselineError("");
      const userInfo = getLoggedInUserInfo();
      const displayDates = getDisplayDates();
      const dateRangeStr = `${formatDate(displayDates.start)} to ${formatDate(displayDates.end)}`;
      const finalName = recordName || dateRangeStr;
      // Check for duplicate: same name, same date range, AND same number of selected days
      const isDuplicate = baselines.some(bl => {
        const existingName = (bl.currentName || bl.originalName || "").trim().toLowerCase();
        const existingRange = (bl.dateRange || "").trim().toLowerCase();
        
        // 1. Check name and date range
        if (existingName !== finalName.trim().toLowerCase() || existingRange !== dateRangeStr.trim().toLowerCase()) {
          return false;
        }

        // 2. Check if the number of selected/included days is the same
        const newDaysCount = tableRows.filter(r => !globalDeselected.has(r.date) && (!r.record || r.record.dayInclude !== 'no')).length;
        const existingRows = bl.tableRows || [];
        const existingDaysCount = existingRows.filter(r => !r.isDeselected && (!r.record || r.record.dayInclude !== 'no')).length;

        return newDaysCount === existingDaysCount;
      });
      if (isDuplicate) {
        setSaveBaselineError("A record with this name, date range, and day selections already exists. Please enter a different name.");
        setSavingBaseline(false);
        return;
      }

      const baselinePayload = {
        userId: decodeURIComponent(userId),
        dateRange: dateRangeStr,
        voids: averages.voids,
        leaks: averages.leaks,
        fluid: averages.fluid,
        night: averages.night,
        adminName: userInfo.name,
        adminEmail: userInfo.email,
        study: patientInfo?.studyName || null,
        site: patientInfo?.siteName || null,
        patientName: patientInfo?.patientName || null,
        recordName: finalName,
        tableRows: tableRows.map(row => ({
          date: row.date,
          record: row.record,
          isDeselected: globalDeselected.has(row.date)
        })),
        compliance_status: patientInfo?.compliance_status || "no-data",
        compliance_issues: patientInfo?.compliance_issues || "[]",
        latestActivity: patientInfo?.latestActivity || "No Activity"
      };

      const result = await saveBaseline(baselinePayload);
      if (result) {
        setBaselines(prev => [result.data, ...prev]);
        setShowSaveBaselineModal(false);
        toast.success("Frozen record saved successfully!");
      } else {
        toast.error("Failed to save frozen record.");
      }
    } catch (err) {
      console.error("Error saving frozen record:", err);
      toast.error("Failed to save frozen record.");
    } finally {
      setSavingBaseline(false);
    }
  };

  const downloadBaselinePDF = (bl) => {
    setBaselineToDownload(bl);
    setShowCompliancePromptModal(true);
  };

  const executeBaselineDownload = (bl, includeCompliance) => {
    if (!bl) return;
    const safePatientName = (patientInfo?.patientName || patientInfo?.patientId || bl.userId || "patient").replace(/[\s_]+/g, "-").replace(/[^a-zA-Z0-9@.-]/g, "");
    const recordNameStr = (bl.currentName || bl.originalName || "frozen-record").replace(/[\s_]+/g, "-").replace(/[^a-zA-Z0-9.-]/g, "-");
    const fileName = `${safePatientName}-summary-${recordNameStr}`;

    const snapshottedTableRows = bl.tableRows || [];
    const snapshottedExcludedDates = new Set(
      snapshottedTableRows.filter(r => r.isDeselected).map(r => r.date)
    );

    generateUserAnalysisPDF({
      patientInfo: {
        studyName: bl.study || patientInfo?.studyName || "EVANESCE II",
        siteName: bl.site || patientInfo?.siteName || "",
        patientId: patientInfo?.patientId || bl.userId,
        patientName: bl.patientName || patientInfo?.patientName || "",
        status: "Frozen Record",
        dateRange: bl.dateRange,
        includeCompliance: includeCompliance,
        compliance_status: bl.compliance_status || "no-data",
        compliance_issues: bl.compliance_issues || "[]",
        latestActivity: bl.latestActivity || "No Activity"
      },
      tableRows: snapshottedTableRows,
      averages: {
        voids: bl.voids,
        leaks: bl.leaks,
        fluid: bl.fluid,
        night: bl.night
      },
      excludedDates: snapshottedExcludedDates,
      startDate: null,
      endDate: null,
      globalAdded: new Set(),
      fileName: fileName,
      shouldSave: true
    });
  };

  const downloadBaselineAveragesPDF = () => {
    if (!baselines || baselines.length === 0) return;
    const count = baselines.length;
    const avg = {
      voids: (baselines.reduce((acc, curr) => acc + Number(curr.voids || 0), 0) / count).toFixed(1),
      leaks: (baselines.reduce((acc, curr) => acc + Number(curr.leaks || 0), 0) / count).toFixed(1),
      fluid: (baselines.reduce((acc, curr) => acc + Number(curr.fluid || 0), 0) / count).toFixed(1),
      night: (baselines.reduce((acc, curr) => acc + Number(curr.night || 0), 0) / count).toFixed(1)
    };

    const safePatientName = (patientInfo?.patientName || patientInfo?.patientId || "patient").replace(/[\s_]+/g, "-").replace(/[^a-zA-Z0-9@.-]/g, "");
    const d = new Date();
    const todayStr = `${d.toLocaleDateString('en-US', { month: 'short' })}-${d.getDate()}-${d.getFullYear()}`;
    const fileName = `${safePatientName}-summary-averages-${todayStr}`;

    generateUserAnalysisPDF({
      patientInfo: {
        studyName: patientInfo?.studyName || "EVANESCE II",
        siteName: patientInfo?.siteName || "",
        patientId: patientInfo?.patientId || decodeURIComponent(userId),
        patientName: patientInfo?.patientName || "",
        status: "Total Frozen Record Averages",
        dateRange: `Average of ${count} frozen records`
      },
      tableRows: [],
      averages: {
        voids: avg.voids,
        leaks: avg.leaks,
        fluid: avg.fluid,
        night: avg.night
      },
      excludedDates: new Set(),
      startDate: null,
      endDate: null,
      globalAdded: new Set(),
      fileName: fileName,
      shouldSave: true
    });
  };

  const handleRenameBaseline = async (recordId, newName) => {
    try {
      const userInfo = getLoggedInUserInfo();
      const result = await renameBaseline(recordId, newName, userInfo.name, userInfo.email);
      if (result && result.success) {
        setBaselines(prev => prev.map(b => b._id === recordId ? result.data : b));
        return { success: true };
      }
      return { success: false, message: "Rename API returned success: false" };
    } catch (err) {
      console.error("Error renaming baseline:", err);
      return { success: false, message: err.message };
    }
  };

  const handleDeleteBaseline = (recordId) => {
    if (!isAdmin) return;
    setRecordToDelete(recordId);
    setShowDeleteModal(true);
  };

  const confirmDeleteBaseline = async () => {
    if (!recordToDelete) return;
    if (!isAdmin || !recordToDelete) return;
    try {
      const result = await deleteBaseline(recordToDelete);
      if (result !== null) {
        setBaselines(prev => prev.filter(b => b._id !== recordToDelete));
        toast.success("Frozen record deleted successfully!");
      } else {
        toast.error("Failed to delete frozen record.");
      }
    } catch (err) {
      console.error("Error deleting frozen record:", err);
      toast.error("Failed to delete frozen record.");
    } finally {
      setShowDeleteModal(false);
      setRecordToDelete(null);
    }
  };

  const executeDownloadPDF = async () => {
    setShowDownloadModal(false);
    generateUserAnalysisPDF({
      patientInfo,
      tableRows,
      averages,
      globalDeselected,
      globalAdded,
      startDate,
      endDate,
      fileName: pdfFileName,
    });

    const userInfo = getLoggedInUserInfo();
    await addAnalysisAuditLog({
      userId: decodeURIComponent(userId),
      adminEmail: userInfo.email,
      adminName: userInfo.name,
      action: "PDF Download",
      source: "7 Day Analysis",
    });
  };

  return (
    <div className="container" style={{ padding: '80px 20px', maxWidth: '100%' }}>
      <Header
        actions={
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              className="button secondary"
              onClick={() => navigate(`/study/${studyId}/${encodeURIComponent(siteId)}`)}
            >
              Back to Site
            </button>

          </div>
        }
      />

      {loading ? (
        <div className="spinner-center" style={{ minHeight: "600px" }}>
          <Spinner label="Loading analysis data..." />
        </div>
      ) : error ? (
        <div className="panel" style={{ marginTop: 16, color: "red" }}>{error}</div>
      ) : (
        <div className="panel" style={{ marginTop: '20px' }}>
          {patientInfo && (
            <div style={{ marginBottom: '20px', lineHeight: '1.6' }}>
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>Patient: <span style={{ color: '#2563eb' }}>{patientInfo.patientName || patientInfo.patientId}</span></div>
              <div style={{ color: '#475569' }}><strong>Study:</strong> <span style={{ color: '#EDC24B' }}>{patientInfo.studyName}</span></div>
              <div style={{ color: '#475569' }}><strong>Site:</strong> <span style={{ color: '#EDC24B' }}>{patientInfo.siteName}</span></div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <strong>Selected Days:</strong>
                <span

                  style={{
                    background: '#f1f5f9',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowCalendar(true)}
                >
                  [ {startDate ? formatDate(getDisplayDates().start) : ''} &rarr; {endDate ? formatDate(getDisplayDates().end) : ''} ]
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', paddingLeft: '4px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Included:
                  <strong style={{ background: '#ecfdf5', color: '#059669', padding: '2px 8px', borderRadius: '4px', border: '1px solid #a7f3d0' }}>
                    {dateRange.filter(d => !globalDeselected.has(d)).length} days
                  </strong>
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Excluded:
                  <strong style={{ background: '#fef2f2', color: '#dc2626', padding: '2px 8px', borderRadius: '4px', border: '1px solid #fecaca' }}>
                    {globalDeselected.size} days
                  </strong>
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <strong>Adjust Window:</strong>
              <button
                className="button secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px' }}
                onClick={() => setShowCalendar(true)}
              >
                <MdCalendarToday /> Change Range
              </button>
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 14px',
            margin: '0 0 16px 0',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#92400e',
            fontWeight: 500,
          }}>
            <span style={{ fontSize: '14px' }}>*</span>
            Summary data till {' '}
            <strong>
              {(() => {
                const yesterday = new Date();
                yesterday.setHours(0, 0, 0, 0);
                yesterday.setDate(yesterday.getDate() - 1);
                const yesterdayStr = formatLocalISO(yesterday);
                const lastRecord = [...activityData]
                  .map(a => a.date)
                  .filter(Boolean)
                  .sort((a, b) => new Date(b + 'T00:00:00') - new Date(a + 'T00:00:00'))[0];
                const displayDate = lastRecord && lastRecord <= yesterdayStr ? lastRecord : yesterdayStr;
                return new Date(displayDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
              })()}
            </strong>

          </div>

          <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', border: '1px solid #e2e8f0', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 600, color: '#334155' }}>Daily Averages:</div>
                <div> Voids: <strong>{averages.voids}</strong></div>
                <div>Leaks: <strong>{averages.leaks}</strong></div>
                <div>Fluids (oz): <strong>{averages.fluid}</strong></div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                className="button primary"
                style={{ border: 'none' }}
                onClick={handleDownloadPDF}
                disabled={!patientInfo || tableRows.length === 0}
                title="Download Analysis PDF"
              >
                Average Summary download
              </button>
              <button
                className="button primary"
                style={{ border: 'none' }}
                onClick={triggerSaveBaseline}
                disabled={!patientInfo || tableRows.length === 0 || savingBaseline}
                title="Save Current Average as Frozen Record"
              >
                {savingBaseline ? "Saving..." : "Save - Freeze Record to PDF"}
              </button>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              cursor: 'pointer',
              userSelect: 'none',
              padding: '10px 0'
            }}
            onClick={() => setShowAnalysisTable(!showAnalysisTable)}
          >
            {showAnalysisTable ? <MdExpandLess size={24} color="#64748b" /> : <MdExpandMore size={24} color="#64748b" />}
            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.25rem', fontWeight: '600' }}>Detailed Analytics Table</h3>
          </div>

          {showAnalysisTable && (
            <div className="table-wrapper" style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th></th>
                    <th>Date</th>
                    <th>Voids</th>
                    <th>Leaks</th>
                    <th>Fluids (oz)</th>
                    <th>Status</th>
                    <th>Reason</th>
                    <th>Action</th>
                    <th>Audit Logs</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, idx) => {
                    const rec = row.record;

                    const isExcluded = (rec && rec.dayInclude === "no") || globalDeselected.has(row.date);

                    let status = "Included";
                    let statusColor = '#16a34a';

                    if (isExcluded) {
                      status = "Excluded";
                      statusColor = '#dc2626';
                    }

                    return (
                      <React.Fragment key={idx}>
                        <tr>
                          <td data-label="Select">
                            <input
                              type="checkbox"
                              disabled={rec?.dayInclude === "no"}
                              checked={rec?.dayInclude === "no" ? false : !globalDeselected.has(row.date)}
                              onChange={(e) => {
                                const newSet = new Set(globalDeselected);
                                if (!e.target.checked) {
                                  newSet.add(row.date);
                                } else {
                                  newSet.delete(row.date);
                                }
                                setGlobalDeselected(newSet);
                                try {
                                  localStorage.setItem(`excludedDates_${decodedUserId}`, JSON.stringify(Array.from(newSet)));
                                } catch (err) { }
                              }}
                            />
                          </td>
                          <td data-label="Date"><strong>{formatDate(row.date)}</strong></td>
                          <td data-label=" Voids">{rec?.voidActivityCount ?? "0"}</td>
                          <td data-label="Leaks">{rec?.totalLeaks ?? "0"}</td>
                          <td data-label="Fluids (oz)">{rec?.intakeMl != null ? rec.intakeMl : "0"}</td>
                          <td data-label="Status">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: statusColor, fontWeight: 500 }}>
                              {status}
                            </span>
                          </td>
                          <td data-label="Reason">
                            {rec?.dayInclude === "no" ? (
                              <div
                                style={{ cursor: 'pointer', display: 'inline-block' }}
                                onClick={() => {
                                  setExcludeModalDate(row.date);
                                  const predefined = ["app malfunction", "incomplete day", "subject non-compliance"];
                                  if (rec.dayexcludeReason && !predefined.includes(rec.dayexcludeReason)) {
                                    setExcludeReason("other");
                                    setOtherReasonText(rec.dayexcludeReason);
                                  } else {
                                    setExcludeReason(rec.dayexcludeReason || "");
                                    setOtherReasonText("");
                                  }
                                }}
                                title="Click to edit reason"
                              >
                                {rec?.dayexcludeReason ? (
                                  <span style={{ fontSize: '14px', textTransform: 'capitalize' }}>{rec.dayexcludeReason}</span>
                                ) : (
                                  <span style={{ color: '#aaa' }}>-</span>
                                )}
                              </div>
                            ) : (
                              rec?.dayexcludeReason ? (
                                <span style={{ fontSize: '14px', textTransform: 'capitalize' }}>{rec.dayexcludeReason}</span>
                              ) : (
                                <span style={{ color: '#aaa' }}>-</span>
                              )
                            )}
                            {rec?.dayIncludeUserName && (
                              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                                By: {rec.dayIncludeUserName}

                              </div>
                            )}
                          </td>
                          <td data-label="Action" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>

                            <select
                              value={rec?.dayInclude || "yes"}
                              onChange={(e) => {
                                if (e.target.value === "no") {
                                  setExcludeModalDate(row.date);
                                  setExcludeReason("");
                                  setOtherReasonText("");
                                } else if (e.target.value === "yes") {
                                  handleDayIncludeChange(row.date, e.target.value);
                                }
                              }}
                              style={{
                                padding: '6px 10px',
                                borderRadius: '4px',
                                border: '1px solid #cbd5e1',
                                background: (rec?.dayInclude === 'no' || (!rec && globalDeselected.has(row.date))) ? '#fef2f2' : '#f0fdf4',
                                width: '100%',
                                maxWidth: '200px',
                                fontSize: '0.85rem'
                              }}
                            >
                              <option value="yes">Include</option>
                              <option value="no">Exclude</option>
                            </select>
                          </td>
                          <td data-label="Audit Logs">
                            <span className="icon-button" style={{ cursor: 'pointer' }} onClick={() => toggleLogs(row.date)}>
                              {expandedLogs[row.date]?.expanded ? (
                                <MdClearAll color="#2563eb" size={23} />
                              ) : (
                                <TbLogs color="#2563eb" size={23} />
                              )}
                            </span>
                          </td>
                        </tr>
                        {expandedLogs[row.date]?.expanded && (
                          <AnalysisLogsTable expandedLogs={expandedLogs} userId={row.date} capitalize={capitalize} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <BaselineSection
            baselines={baselines}
            showBaselineHistory={showBaselineHistory}
            setShowBaselineHistory={setShowBaselineHistory}
            onDownloadPDF={downloadBaselinePDF}
            onDownloadAveragesPDF={downloadBaselineAveragesPDF}
            onRename={handleRenameBaseline}
            onDelete={isAdmin ? handleDeleteBaseline : null}
          />
        </div>
      )}

      {showCalendar && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
          <RangeDeSelectCalendar
            initialRange={{ from: startDate, to: endDate }}
            initialDeselected={globalDeselected}
            initialAdded={globalAdded}
            initialAutoExclude={globalAutoExclude}
            onConfirm={handleCalendarConfirm}
            onClose={() => setShowCalendar(false)}
            activityData={activityData}
            dbExcludedDates={new Set(
              activityData
                .filter(a => a.dayInclude === "no")
                .map(a => a.date)
            )}
          />
        </div>
      )}

      {excludeModalDate && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', width: '400px', maxWidth: '90%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#1e293b' }}>Select Exclude Reason</h3>
            <div style={{ marginBottom: '16px' }}>
              <select
                value={excludeReason}
                onChange={(e) => setExcludeReason(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '14px' }}
              >
                <option value="" disabled>Select Reason...</option>
                <option value="app malfunction">App Malfunction</option>
                <option value="incomplete day">In complete Day</option>
                <option value="subject non-compliance">Subject Non-Compliance</option>
                <option value="other">Other</option>
              </select>
            </div>
            {excludeReason === "other" && (
              <div style={{ marginBottom: '16px' }}>
                <input
                  type="text"
                  placeholder="Specify other reason..."
                  value={otherReasonText}
                  onChange={(e) => setOtherReasonText(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '14px' }}
                />
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px' }}>
              <button
                className="button secondary"
                onClick={() => setExcludeModalDate(null)}
                style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                className="button primary"
                disabled={!excludeReason || (excludeReason === "other" && !otherReasonText)}
                onClick={() => {
                  const finalReason = excludeReason === "other" ? otherReasonText : excludeReason;
                  handleDayIncludeChange(excludeModalDate, "no", finalReason);
                  setExcludeModalDate(null);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: 'none',
                  background: (!excludeReason || (excludeReason === "other" && !otherReasonText)) ? '#94a3b8' : '#2563eb',
                  color: '#fff',
                  cursor: (!excludeReason || (excludeReason === "other" && !otherReasonText)) ? 'not-allowed' : 'pointer'
                }}
              >
                Save Reason
              </button>
            </div>
          </div>
        </div>
      )}

      {showDownloadModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "350px", padding: "20px" }}>
            <h3 style={{ marginBottom: "15px", fontSize: "1rem" }}>Save PDF for {patientInfo?.patientId}</h3>
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "0.85rem", color: "#666" }}>
                File Name:
              </label>
              <input
                type="text"
                value={pdfFileName}
                onChange={(e) => setPdfFileName(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  fontSize: "0.9rem"
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                className="button secondary"
                onClick={() => setShowDownloadModal(false)}
                style={{ fontSize: "0.85rem" }}
              >
                Cancel
              </button>
              <button
                className="button primary"
                onClick={executeDownloadPDF}
                style={{ fontSize: "0.85rem" }}
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {showSaveBaselineModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', width: '400px', maxWidth: '90%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#1e293b' }}>Save Frozen Record</h3>
            <p style={{ fontSize: '0.95rem', color: '#475569', marginBottom: '16px', lineHeight: '1.5' }}>
              Please enter a name for this frozen record.
            </p>
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", marginBottom: "8px", fontSize: "0.85rem", color: "#666" }}>
                Custom Name:
              </label>
              <input
                type="text"
                value={newBaselineName}
                onChange={(e) => {
                  setNewBaselineName(e.target.value);
                  if (saveBaselineError) setSaveBaselineError("");
                }}
                placeholder="Enter baseline name..."
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "4px",
                  border: saveBaselineError ? "1px solid #ef4444" : "1px solid #ddd",
                  fontSize: "0.9rem"
                }}
              />
              {saveBaselineError && (
                <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '6px', fontWeight: '500' }}>
                  {saveBaselineError}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                className="button secondary"
                onClick={() => {
                  setShowSaveBaselineModal(false);
                  setSaveBaselineError("");
                }}
                style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                className="button primary"
                onClick={() => handleSaveBaseline(newBaselineName)}
                disabled={savingBaseline || !newBaselineName.trim()}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: 'none',
                  background: (!newBaselineName.trim() || savingBaseline) ? '#94a3b8' : '#2563eb',
                  color: '#fff',
                  cursor: (!newBaselineName.trim() || savingBaseline) ? 'not-allowed' : 'pointer'
                }}
              >
                {savingBaseline ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
{isAdmin && showDeleteModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', width: '400px', maxWidth: '90%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#1e293b' }}>Confirm Delete</h3>
            <p style={{ fontSize: '0.95rem', color: '#475569', marginBottom: '16px', lineHeight: '1.5' }}>
              Are you sure you want to permanently delete this frozen record? This action cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                className="button secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setRecordToDelete(null);
                }}
                style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                className="button primary"
                onClick={confirmDeleteBaseline}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  border: 'none',
                  background: '#dc2626',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showCompliancePromptModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#fff', padding: '24px', borderRadius: '8px', width: '420px', maxWidth: '90%', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '12px', color: '#1e293b', fontSize: '1.2rem', fontWeight: 600 }}>Download PDF Option</h3>
            <p style={{ fontSize: '0.95rem', color: '#475569', marginBottom: '20px', lineHeight: '1.5' }}>
              Would you like to include the <strong>compliance status and compliance issues</strong> in the downloaded PDF report?
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => setShowCompliancePromptModal(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  color: '#475569',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowCompliancePromptModal(false);
                  executeBaselineDownload(baselineToDownload, false);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#f1f5f9',
                  color: '#0f172a',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                No, Exclude
              </button>
              <button
                onClick={() => {
                  setShowCompliancePromptModal(false);
                  executeBaselineDownload(baselineToDownload, true);
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  background: '#2563eb',
                  color: '#fff',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Yes, Include
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};
